import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getGamesBySeason, realTeams, stageLabel } from "@/utils/nba";
import type { Stage } from "@/types";
import GameCard from "@/components/base/GameCard";

const PAGE_SIZE = 6;

const stageOptions: { value: Stage | "all"; label: string }[] = [
  { value: "all", label: "全部阶段" },
  { value: "regular", label: "常规赛" },
  { value: "playoff", label: "季后赛" },
  { value: "allstar", label: "全明星" },
];

const sortOptions = [
  { value: "time-asc", label: "按时间升序" },
  { value: "time-desc", label: "按时间降序" },
  { value: "importance", label: "按重要性排序" },
];

export default function Category() {
  const { season = "2025-2026" } = useParams();

  const [stage, setStage] = useState<Stage | "all">("all");
  const [teamId, setTeamId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sort, setSort] = useState("time-asc");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useSeo({
    title: `${season} NBA赛事列表 - NBA直播吧`,
    description: `${season} 赛季 NBA 赛事列表，支持按常规赛、季后赛、全明星阶段以及球队和日期范围筛选，快速查找你关注的比赛并查看实时比分。`,
    keywords: `NBA${season}赛程,NBA赛事列表,NBA赛程查询,NBA直播吧,NBA比分`,
  });

  const seasonGames = getGamesBySeason(season);

  const filtered = useMemo(() => {
    let list = seasonGames.filter((game) => {
      if (stage !== "all" && game.stage !== stage) return false;
      if (teamId !== "all" && game.homeTeamId !== teamId && game.awayTeamId !== teamId) return false;
      if (fromDate && game.date < fromDate) return false;
      if (toDate && game.date > toDate) return false;
      return true;
    });
    if (sort === "time-asc") list = [...list].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
    if (sort === "time-desc") list = [...list].sort((a, b) => `${b.date}${b.time}`.localeCompare(`${a.date}${a.time}`));
    if (sort === "importance") list = [...list].sort((a, b) => b.importance - a.importance);
    return list;
  }, [seasonGames, stage, teamId, fromDate, toDate, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setStage("all");
    setTeamId("all");
    setFromDate("");
    setToDate("");
    setSort("time-asc");
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="mx-auto max-w-page px-4 py-8 md:px-6 md:py-10">
      {/* 面包屑 */}
      <nav className="mb-5 flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
        <i className="ri-arrow-right-s-line" />
        <span>赛事分类</span>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900 font-medium">{season}</span>
      </nav>

      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
          {season} 赛季 NBA 赛事列表
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          涵盖 {season} 赛季常规赛、季后赛及全明星赛的全部赛事，可按球队与日期范围筛选，并支持多种排序方式。
        </p>
      </div>

      {/* 筛选栏 */}
      <div className="mb-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground-600">赛季阶段</label>
            <div className="flex flex-wrap gap-1 rounded-md bg-background-100 p-1">
              {stageOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    setStage(option.value);
                    setVisible(PAGE_SIZE);
                  }}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                    stage === option.value
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground-600">球队筛选</label>
            <select
              value={teamId}
              onChange={(event) => {
                setTeamId(event.target.value);
                setVisible(PAGE_SIZE);
              }}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="all">全部球队</option>
              {realTeams().map((team) => (
                <option key={team.id} value={team.id}>{team.fullName}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground-600">开始日期</label>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => {
                setFromDate(event.target.value);
                setVisible(PAGE_SIZE);
              }}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground-600">结束日期</label>
            <input
              type="date"
              value={toDate}
              onChange={(event) => {
                setToDate(event.target.value);
                setVisible(PAGE_SIZE);
              }}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-foreground-600">排序方式</label>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none focus:border-primary-400 cursor-pointer"
            >
              {sortOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-600 transition-colors hover:bg-background-100 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        </div>
      </div>

      {/* 结果统计 */}
      <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
        <p>
          共找到 <span className="font-semibold text-foreground-900">{filtered.length}</span> 场赛事
          {stage !== "all" && <span> · {stageLabel(stage)}</span>}
        </p>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {shown.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="rounded-md bg-primary-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                加载更多赛事
              </button>
            </div>
          )}
          {!hasMore && (
            <p className="mt-8 text-center text-sm text-foreground-400">已显示全部赛事</p>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-inbox-line text-2xl" />
          </span>
          <p className="font-heading text-lg font-semibold text-foreground-900">没有找到匹配的赛事</p>
          <p className="mt-2 max-w-md text-sm text-foreground-500">
            当前筛选条件下暂无赛事，试试更换赛季阶段、球队或日期范围，或直接重置筛选条件。
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-6 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            重置筛选条件
          </button>
        </div>
      )}
    </div>
  );
}